import { useState, useEffect, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X } from "lucide-react";

const TYPES = [
  ["website", "Website"],
  ["webapp", "Web App"],
  ["mobile", "Mobile App"],
  ["drive", "Drive"],
  ["tool", "Tool"],
  ["other", "Other"],
];

const emptyLink = { title: "", url: "", description: "", type: "website", category_id: "", icon: "Link", order: 0 };
const emptyCat = { name: "", description: "", icon: "Folder", accent: "#0F172A", span: 1, order: 0 };

export default function AdminPanel({ open, onOpenChange, categories, links, onChanged }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto border-l border-white/60 bg-white/80 backdrop-blur-2xl sm:max-w-xl"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Manage Portal</SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="links" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="links" data-testid="tab-links">Links</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="links" className="mt-5">
            <LinksTab categories={categories} links={links} onChanged={onChanged} />
          </TabsContent>
          <TabsContent value="categories" className="mt-5">
            <CategoriesTab categories={categories} onChanged={onChanged} />
          </TabsContent>
          <TabsContent value="users" className="mt-5">
            <UsersTab />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function LinksTab({ categories, links, onChanged }) {
  const [form, setForm] = useState({ ...emptyLink });
  const [editId, setEditId] = useState(null);

  const save = async () => {
    if (!form.title || !form.url || !form.category_id) {
      toast.error("Title, URL and category are required");
      return;
    }
    try {
      if (editId) await api.put(`/links/${editId}`, form);
      else await api.post("/links", form);
      toast.success(editId ? "Link updated" : "Link added");
      setForm({ ...emptyLink });
      setEditId(null);
      onChanged();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const edit = (l) => {
    setForm({ title: l.title, url: l.url, description: l.description, type: l.type, category_id: l.category_id, icon: l.icon, order: l.order });
    setEditId(l.id);
  };

  const del = async (id) => {
    await api.delete(`/links/${id}`);
    toast.success("Link removed");
    onChanged();
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3 rounded-2xl border border-white/60 bg-white/60 p-4">
        <div className="flex items-center justify-between">
          <p className="font-heading text-sm font-semibold">{editId ? "Edit link" : "New link"}</p>
          {editId && (
            <button onClick={() => { setForm({ ...emptyLink }); setEditId(null); }} className="text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Input data-testid="link-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Input data-testid="link-url" placeholder="https://…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        <Textarea data-testid="link-desc" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="link-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger data-testid="link-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input placeholder="Lucide icon name (e.g. Globe)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
        <Button data-testid="save-link-btn" onClick={save} className="w-full rounded-full bg-slate-900">
          <Plus className="mr-1 h-4 w-4" /> {editId ? "Update link" : "Add link"}
        </Button>
      </div>

      <div className="space-y-2">
        {links.map((l) => (
          <div key={l.id} className="flex items-center justify-between rounded-xl border border-white/60 bg-white/50 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate font-plex text-sm font-medium">{l.title}</p>
              <p className="truncate font-plex text-xs text-slate-400">{l.url}</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <button data-testid={`edit-link-${l.id}`} onClick={() => edit(l)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-800">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`del-link-${l.id}`} onClick={() => del(l.id)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoriesTab({ categories, onChanged }) {
  const [form, setForm] = useState({ ...emptyCat });
  const [editId, setEditId] = useState(null);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const payload = { ...form, span: Number(form.span) || 1, order: Number(form.order) || 0 };
    try {
      if (editId) await api.put(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Category updated" : "Category added");
      setForm({ ...emptyCat });
      setEditId(null);
      onChanged();
    } catch { toast.error("Save failed"); }
  };

  const edit = (c) => {
    setForm({ name: c.name, description: c.description, icon: c.icon, accent: c.accent, span: c.span, order: c.order });
    setEditId(c.id);
  };

  const del = async (id) => {
    await api.delete(`/categories/${id}`);
    toast.success("Category removed");
    onChanged();
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3 rounded-2xl border border-white/60 bg-white/60 p-4">
        <div className="flex items-center justify-between">
          <p className="font-heading text-sm font-semibold">{editId ? "Edit category" : "New category"}</p>
          {editId && (
            <button onClick={() => { setForm({ ...emptyCat }); setEditId(null); }} className="text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Input data-testid="cat-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input data-testid="cat-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Icon (e.g. Palette)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
          <div className="flex items-center gap-2">
            <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 w-12 cursor-pointer rounded border border-slate-200" />
            <Input placeholder="#0F172A" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" placeholder="Span (1-2)" value={form.span} onChange={(e) => setForm({ ...form, span: e.target.value })} />
          <Input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
        </div>
        <Button data-testid="save-cat-btn" onClick={save} className="w-full rounded-full bg-slate-900">
          <Plus className="mr-1 h-4 w-4" /> {editId ? "Update category" : "Add category"}
        </Button>
      </div>

      <div className="space-y-2">
        {categories.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-white/60 bg-white/50 px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="h-4 w-4 rounded-full" style={{ backgroundColor: c.accent }} />
              <p className="font-plex text-sm font-medium">{c.name}</p>
            </div>
            <div className="flex gap-1">
              <button data-testid={`edit-cat-${c.id}`} onClick={() => edit(c)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-800">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`del-cat-${c.id}`} onClick={() => del(c.id)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-500">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "member" });

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!form.username || !form.password) { toast.error("Username & password required"); return; }
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ username: "", password: "", name: "", role: "member" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3 rounded-2xl border border-white/60 bg-white/60 p-4">
        <p className="font-heading text-sm font-semibold">New user</p>
        <Input data-testid="user-username" placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <Input data-testid="user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input data-testid="user-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="member">Member</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="save-user-btn" onClick={add} className="w-full rounded-full bg-slate-900">
          <Plus className="mr-1 h-4 w-4" /> Create user
        </Button>
      </div>

      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-xl border border-white/60 bg-white/50 px-4 py-3">
            <div>
              <p className="font-plex text-sm font-medium">{u.username}</p>
              <p className="font-plex text-xs text-slate-400">{u.role}{u.name ? ` · ${u.name}` : ""}</p>
            </div>
            <button data-testid={`del-user-${u.id}`} onClick={() => del(u.id)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
